import React, { useEffect, useRef, useState } from 'react'

import Moment from 'moment'

import FAIcon from '../FAIcon'

import { DOCUMENT_CATEGORIES, generateSuggestedDocumentName, resolveDocumentName } from './DocumentMetadataModal'

export { DOCUMENT_CATEGORIES, generateSuggestedDocumentName, resolveDocumentName }

const CROP_PRESETS = {
  portraitDoc: { label: 'Portrait Doc (8.5×11)', top: 5, bottom: 5, left: 18, right: 18, orientation: 'portrait' },
  fullFrame: { label: 'Full Frame', top: 4, bottom: 4, left: 4, right: 4, orientation: 'portrait' },
  tightCrop: { label: 'Tight Crop', top: 12, bottom: 12, left: 22, right: 22, orientation: 'portrait' },
  wideSlip: { label: 'Wide Slip (Landscape)', top: 12, bottom: 12, left: 8, right: 8, orientation: 'landscape' },
}

const CameraScanModal = ({
  show = true,
  onHide,
  onUpload,
  associatedProviderName = '',
  associatedProviderId = null,
  appointmentId = null,
  providers = [],
  simulatePermissionDenied = false,
  initialPages = [{ id: 1, name: 'Page 1' }],
}) => {
  const [pages, setPages] = useState(initialPages)
  const [activePreviewPageId, setActivePreviewPageId] = useState(null)
  const [retakingPageId, setRetakingPageId] = useState(null)
  const [showLiveCamera, setShowLiveCamera] = useState(true)
  const [shutterFlash, setShutterFlash] = useState(false)
  const [captureBanner, setCaptureBanner] = useState(null)

  // Camera Orientation, Rotation, Zoom, Crop, and Contrast Controls
  const [orientation, setOrientation] = useState('portrait') // 'portrait' | 'landscape'
  const [rotation, setRotation] = useState(0) // 0 | 90 | 180 | 270
  const [flipHorizontal, setFlipHorizontal] = useState(false)
  const [zoom, setZoom] = useState(1.0) // 1.0 to 3.0
  const [cropPreset, setCropPreset] = useState('portraitDoc')
  const [cropInsets, setCropInsets] = useState({ top: 5, bottom: 5, left: 18, right: 18 })
  const [showCropSliders, setShowCropSliders] = useState(false)
  const [enhanceContrast, setEnhanceContrast] = useState(true)

  const [category, setCategory] = useState('Work Status')
  const [providerName, setProviderName] = useState(associatedProviderName)
  const [selectedProviderId, setSelectedProviderId] = useState(associatedProviderId)
  const [docDate, setDocDate] = useState(() => Moment().format('YYYY-MM-DD'))
  const [docName, setDocName] = useState(() =>
    generateSuggestedDocumentName('Work Status', associatedProviderName, new Date()),
  )
  const [hasUserEditedName, setHasUserEditedName] = useState(false)
  const [permissionError, setPermissionError] = useState(() =>
    simulatePermissionDenied ? 'NotAllowedError: Camera permission denied' : null,
  )
  const [uploadError, setUploadError] = useState(null)
  const [isCapturing, setIsCapturing] = useState(false)

  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const nativeInputRef = useRef(null)
  const fallbackInputRef = useRef(null)
  const nameInputRef = useRef(null)
  const dragCornerRef = useRef(null)
  const viewportBoxRef = useRef(null)

  // Initialize and update metadata when props change
  useEffect(() => {
    if (show) {
      setPages(initialPages)
      setActivePreviewPageId(null)
      setRetakingPageId(null)
      setShowLiveCamera(true)
      setCaptureBanner(null)
      setUploadError(null)
      setProviderName(associatedProviderName || '')
      setSelectedProviderId(associatedProviderId || null)
      if (!hasUserEditedName) {
        setDocName(generateSuggestedDocumentName(category, associatedProviderName, docDate))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, associatedProviderName, associatedProviderId])

  // Ensure live camera video element retains stream when toggling back from captured preview
  useEffect(() => {
    if (show && showLiveCamera && videoRef.current && streamRef.current) {
      if (videoRef.current.srcObject !== streamRef.current) {
        videoRef.current.srcObject = streamRef.current
      }
      if (typeof videoRef.current.play === 'function') {
        videoRef.current.play().catch(() => {})
      }
    }
  }, [show, showLiveCamera])

  // Programmatic focus (zero JSX autoFocus)
  useEffect(() => {
    if (show && nameInputRef.current) {
      nameInputRef.current.focus()
    }
  }, [show])

  // Apply hardware camera zoom constraint if supported by video track
  useEffect(() => {
    if (!streamRef.current) return
    try {
      const [videoTrack] = streamRef.current.getVideoTracks ? streamRef.current.getVideoTracks() : []
      if (
        videoTrack &&
        typeof videoTrack.getCapabilities === 'function' &&
        typeof videoTrack.applyConstraints === 'function'
      ) {
        const capabilities = videoTrack.getCapabilities()
        if (capabilities && capabilities.zoom) {
          const clampedZoom = Math.min(capabilities.zoom.max || 3, Math.max(capabilities.zoom.min || 1, zoom))
          videoTrack.applyConstraints({ advanced: [{ zoom: clampedZoom }] }).catch(() => {})
        }
      }
    } catch (_e) {
      // Digital CSS/canvas zoom handles all devices regardless of hardware support
    }
  }, [zoom])

  // Handle live camera media stream lifecycle
  useEffect(() => {
    if (!show) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
      return
    }

    if (simulatePermissionDenied) {
      setPermissionError('NotAllowedError: Camera permission denied')
      return
    }

    let isSubscribed = true

    const startCamera = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          return
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1440 },
          },
          audio: false,
        })

        if (!isSubscribed) {
          stream.getTracks().forEach(t => t.stop())
          return
        }

        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.muted = true
          videoRef.current.srcObject = stream
          try {
            await videoRef.current.play()
          } catch (_e) {
            // Autoplay may be restricted until interaction
          }
        }
        setPermissionError(null)
      } catch (err) {
        if (isSubscribed) {
          setPermissionError(
            err.name === 'NotAllowedError'
              ? 'NotAllowedError: Camera permission denied'
              : err.message || 'Camera access blocked.',
          )
        }
      }
    }

    startCamera()

    return () => {
      isSubscribed = false
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
    }
  }, [show, simulatePermissionDenied])

  // Re-attach stream if user toggles back to live camera
  useEffect(() => {
    if (showLiveCamera && streamRef.current && videoRef.current && videoRef.current.srcObject !== streamRef.current) {
      videoRef.current.muted = true
      videoRef.current.srcObject = streamRef.current
      try {
        const playPromise = videoRef.current.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {})
        }
      } catch (_e) {
        // Ignore in non-media JSDOM environments
      }
    }
  }, [showLiveCamera])

  if (!show) return null

  const handleRotateLeft = () => {
    setRotation(prev => (prev - 90 + 360) % 360)
  }

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360)
  }

  const handleOrientationToggle = nextOrientation => {
    setOrientation(nextOrientation)
    if (nextOrientation === 'portrait') {
      setCropPreset('portraitDoc')
      setCropInsets({ top: 5, bottom: 5, left: 18, right: 18 })
    } else {
      setCropPreset('wideSlip')
      setCropInsets({ top: 10, bottom: 10, left: 6, right: 6 })
    }
  }

  const handlePresetSelect = presetKey => {
    const preset = CROP_PRESETS[presetKey]
    if (!preset) return
    setCropPreset(presetKey)
    setOrientation(preset.orientation)
    setCropInsets({
      top: preset.top,
      bottom: preset.bottom,
      left: preset.left,
      right: preset.right,
    })
  }

  const handleZoomStep = delta => {
    setZoom(prev => {
      const next = Math.round((prev + delta) * 100) / 100
      return Math.min(3.0, Math.max(1.0, next))
    })
  }

  // Interactive drag on crop box corners
  const handleCornerMouseDown = (corner, e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCornerRef.current = corner

    const handleMouseMove = moveEvt => {
      if (!dragCornerRef.current || !viewportBoxRef.current) return
      const rect = viewportBoxRef.current.getBoundingClientRect()
      if (!rect.width || !rect.height) return

      const relX = Math.min(100, Math.max(0, ((moveEvt.clientX - rect.left) / rect.width) * 100))
      const relY = Math.min(100, Math.max(0, ((moveEvt.clientY - rect.top) / rect.height) * 100))

      setCropPreset('custom')
      setCropInsets(prev => {
        const next = { ...prev }
        const c = dragCornerRef.current
        if (c.includes('top')) {
          next.top = Math.min(45, Math.max(2, Math.round(relY)))
        }
        if (c.includes('bottom')) {
          next.bottom = Math.min(45, Math.max(2, Math.round(100 - relY)))
        }
        if (c.includes('left')) {
          next.left = Math.min(45, Math.max(2, Math.round(relX)))
        }
        if (c.includes('right')) {
          next.right = Math.min(45, Math.max(2, Math.round(100 - relX)))
        }
        return next
      })
    }

    const handleMouseUp = () => {
      dragCornerRef.current = null
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }

  const handleCategoryChange = e => {
    const val = e.target.value
    setCategory(val)
    if (!hasUserEditedName || !docName.trim()) {
      setDocName(generateSuggestedDocumentName(val, providerName, docDate))
    }
  }

  const handleProviderSelectChange = e => {
    const pId = e.target.value
    setSelectedProviderId(pId ? parseInt(pId, 10) : null)
    const match = providers.find(p => String(p.id) === String(pId))
    const pName = match ? match.name || `${match.firstName || ''} ${match.lastName || ''}`.trim() : ''
    setProviderName(pName)
    if (!hasUserEditedName || !docName.trim()) {
      setDocName(generateSuggestedDocumentName(category, pName, docDate))
    }
  }

  const handleProviderNameChange = e => {
    const val = e.target.value
    setProviderName(val)
    if (!hasUserEditedName || !docName.trim()) {
      setDocName(generateSuggestedDocumentName(category, val, docDate))
    }
  }

  const handleDateChange = e => {
    const val = e.target.value
    setDocDate(val)
    if (!hasUserEditedName || !docName.trim()) {
      setDocName(generateSuggestedDocumentName(category, providerName, val))
    }
  }

  const handleNameChange = e => {
    setHasUserEditedName(true)
    setDocName(e.target.value)
  }

  const handleDeletePage = pageId => {
    setPages(prev => {
      const filtered = prev.filter(p => p.id !== pageId)
      if (activePreviewPageId === pageId) {
        const lastWithImg = [...filtered].reverse().find(p => p.dataUrl)
        setActivePreviewPageId(lastWithImg ? lastWithImg.id : null)
        if (!lastWithImg) setShowLiveCamera(true)
      }
      return filtered
    })
  }

  const handleMovePage = (currentIndex, targetIndex) => {
    setPages(prev => {
      if (targetIndex < 0 || targetIndex >= prev.length) return prev
      const updated = [...prev]
      const [moved] = updated.splice(currentIndex, 1)
      updated.splice(targetIndex, 0, moved)
      return updated
    })
  }

  // Rotate an already-captured page image 90 degrees clockwise or counter-clockwise
  const handleRotateCapturedPage = (pageId, deltaDeg) => {
    const targetPage = pages.find(p => p.id === pageId)
    if (!targetPage || !targetPage.dataUrl || typeof Image === 'undefined') return

    const img = new Image()
    img.onload = () => {
      const rotCanvas = document.createElement('canvas')
      const isRightAngle = Math.abs(deltaDeg) % 180 === 90
      rotCanvas.width = isRightAngle ? img.height : img.width
      rotCanvas.height = isRightAngle ? img.width : img.height
      const ctx = rotCanvas.getContext('2d')
      if (ctx) {
        ctx.translate(rotCanvas.width / 2, rotCanvas.height / 2)
        ctx.rotate((deltaDeg * Math.PI) / 180)
        ctx.drawImage(img, -img.width / 2, -img.height / 2)
        const rotatedDataUrl = rotCanvas.toDataURL('image/jpeg', 0.92)
        setPages(prev => prev.map(p => (p.id === pageId ? { ...p, dataUrl: rotatedDataUrl } : p)))
      }
    }
    img.src = targetPage.dataUrl
  }

  // Frame capture with rotation, zoom, adjustable crop, and contrast enhancement: (pixel - 128) * 1.4 + 128
  const captureCurrentVideoFrame = () => {
    const video = videoRef.current
    const canvas = canvasRef.current || document.createElement('canvas')
    const rawW = video && video.videoWidth > 0 ? video.videoWidth : 1280
    const rawH = video && video.videoHeight > 0 ? video.videoHeight : 960

    const isRotated90or270 = rotation === 90 || rotation === 270
    const orientedW = isRotated90or270 ? rawH : rawW
    const orientedH = isRotated90or270 ? rawW : rawH

    canvas.width = orientedW
    canvas.height = orientedH

    const ctx = canvas.getContext('2d')
    if (video && video.videoWidth > 0) {
      if (typeof ctx.save === 'function' && typeof ctx.translate === 'function') {
        ctx.save()
        ctx.translate(orientedW / 2, orientedH / 2)
        if (rotation !== 0 && typeof ctx.rotate === 'function') {
          ctx.rotate((rotation * Math.PI) / 180)
        }
        if ((flipHorizontal || zoom > 1) && typeof ctx.scale === 'function') {
          ctx.scale(flipHorizontal ? -zoom : zoom, zoom)
        }
        ctx.drawImage(video, -rawW / 2, -rawH / 2, rawW, rawH)
        ctx.restore()
      } else {
        ctx.drawImage(video, 0, 0, orientedW, orientedH)
      }
    } else {
      // Fallback simulated document surface for headless/test environments
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, orientedW, orientedH)
      ctx.fillStyle = '#222222'
      ctx.font = '32px sans-serif'
      ctx.fillText('Scanned Document Frame', 60, 120)
    }

    // 1. Crop according to user's interactive cropInsets (defaulting to >=5% boundary margins)
    const topPct = Math.min(45, Math.max(2, cropInsets.top || 5)) / 100
    const bottomPct = Math.min(45, Math.max(2, cropInsets.bottom || 5)) / 100
    const leftPct = Math.min(45, Math.max(2, cropInsets.left || 5)) / 100
    const rightPct = Math.min(45, Math.max(2, cropInsets.right || 5)) / 100

    const insetX = Math.round(orientedW * leftPct)
    const insetY = Math.round(orientedH * topPct)
    const cropW = Math.max(64, Math.round(orientedW * (1 - leftPct - rightPct)))
    const cropH = Math.max(64, Math.round(orientedH * (1 - topPct - bottomPct)))

    const croppedCanvas = document.createElement('canvas')
    croppedCanvas.width = cropW
    croppedCanvas.height = cropH
    const croppedCtx = croppedCanvas.getContext('2d')
    croppedCtx.drawImage(canvas, insetX, insetY, cropW, cropH, 0, 0, cropW, cropH)

    // 2. Contrast Enhancement Formula: (pixel - 128) * 1.4 + 128
    if (enhanceContrast) {
      const imgData = croppedCtx.getImageData(0, 0, cropW, cropH)
      const data = imgData.data
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, Math.max(0, Math.round((data[i] - 128) * 1.4 + 128))) // R
        data[i + 1] = Math.min(255, Math.max(0, Math.round((data[i + 1] - 128) * 1.4 + 128))) // G
        data[i + 2] = Math.min(255, Math.max(0, Math.round((data[i + 2] - 128) * 1.4 + 128))) // B
      }
      croppedCtx.putImageData(imgData, 0, 0)
    }

    const dataUrl = croppedCanvas.toDataURL('image/jpeg', 0.92)
    return dataUrl
  }

  const dataUrlToBlob = async dataUrl => {
    if (typeof dataUrl !== 'string' || !dataUrl.trim()) {
      throw new Error('Invalid image data URL')
    }
    if (dataUrl.startsWith('data:')) {
      const parts = dataUrl.split(',')
      if (parts.length === 2 && typeof atob === 'function') {
        const mimeMatch = parts[0].match(/:(.*?);/)
        const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'
        const bstr = atob(parts[1])
        let n = bstr.length
        const u8arr = new Uint8Array(n)
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n)
        }
        return new Blob([u8arr], { type: mime })
      }
      throw new Error('Malformed data URL payload')
    }
    if (typeof fetch === 'function') {
      const res = await fetch(dataUrl)
      if (!res || !res.ok) {
        throw new Error('Failed to read captured image blob')
      }
      return res.blob()
    }
    throw new Error('Unable to convert captured image data to Blob')
  }

  const handleCaptureFrame = () => {
    setIsCapturing(true)
    setUploadError(null)
    setShutterFlash(true)
    setTimeout(() => setShutterFlash(false), 220)

    try {
      const dataUrl = captureCurrentVideoFrame()
      const newPageId = Date.now() + Math.random()

      setPages(prev => {
        if (retakingPageId && prev.some(p => p.id === retakingPageId)) {
          const targetIdx = prev.findIndex(p => p.id === retakingPageId)
          const pageNum = targetIdx + 1
          setCaptureBanner(`Updated Page ${pageNum} (${orientation}, ${rotation}°, ${zoom.toFixed(1)}x zoom)`)
          return prev.map((p, idx) =>
            p.id === retakingPageId
              ? {
                  id: newPageId,
                  name: `Page ${idx + 1}`,
                  dataUrl,
                }
              : p,
          )
        }
        const isOnlyPlaceholder = prev.length === 1 && !prev[0].dataUrl && !prev[0].file
        const pageNumber = isOnlyPlaceholder ? 1 : prev.length + 1
        const newPage = {
          id: newPageId,
          name: `Page ${pageNumber}`,
          dataUrl,
        }
        setCaptureBanner(`Captured Page ${pageNumber} (${orientation}, ${rotation}°, ${zoom.toFixed(1)}x zoom)`)
        if (isOnlyPlaceholder) {
          return [newPage]
        }
        return [...prev, newPage]
      })

      setActivePreviewPageId(newPageId)
      setRetakingPageId(null)
      setShowLiveCamera(false)
    } finally {
      setIsCapturing(false)
    }
  }

  // Native mobile camera input or fallback file upload handler
  const handleFileCapture = e => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    setUploadError(null)

    const newPageId = Date.now() + Math.random()
    const initialPage = {
      id: newPageId,
      name: file.name || `Page ${pages.length + 1}`,
      dataUrl: null,
      file,
    }
    setPages(prev => {
      if (prev.length === 1 && !prev[0].dataUrl && !prev[0].file) {
        return [initialPage]
      }
      return [...prev, initialPage]
    })
    setActivePreviewPageId(newPageId)
    setRetakingPageId(null)
    setShowLiveCamera(false)
    setCaptureBanner(`Loaded "${file.name}" for scan upload`)

    if (typeof FileReader !== 'undefined') {
      const reader = new FileReader()
      reader.onload = ev => {
        const dataUrl = ev?.target?.result
        if (dataUrl) {
          setPages(prev => prev.map(p => (p.id === newPageId ? { ...p, dataUrl } : p)))
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async () => {
    setUploadError(null)
    const finalName = resolveDocumentName(docName, category, providerName || associatedProviderName, docDate)

    const validPages = pages.filter(p => p && (p.file instanceof File || p.file instanceof Blob || p.dataUrl))
    if (validPages.length === 0) {
      setUploadError('Please capture at least one page or select an image file before uploading.')
      return
    }

    const uploadedFiles = []
    try {
      for (let idx = 0; idx < validPages.length; idx += 1) {
        const page = validPages[idx]
        const pageSuffix = validPages.length > 1 ? ` - Page ${idx + 1}` : ''
        let pageFile = null

        if (page.file instanceof File || page.file instanceof Blob) {
          const orig = page.file
          const ext = orig.name && orig.name.includes('.') ? orig.name.split('.').pop() : 'jpg'
          pageFile = new File([orig], `${finalName}${pageSuffix}.${ext}`, { type: orig.type || 'image/jpeg' })
          if (page.dataUrl) {
            pageFile.dataUrl = page.dataUrl
          }
        } else if (page.dataUrl) {
          const blob = await dataUrlToBlob(page.dataUrl)
          pageFile = new File([blob], `${finalName}${pageSuffix}.jpg`, { type: blob.type || 'image/jpeg' })
          pageFile.dataUrl = page.dataUrl
        }

        if (!pageFile) {
          throw new Error(`Unable to process page ${idx + 1}`)
        }
        uploadedFiles.push(pageFile)
      }
    } catch (_err) {
      setUploadError('Unable to process captured image data. Please retake the photo or select a valid image file.')
      return
    }

    const targetPage =
      validPages.find(p => p.id === activePreviewPageId) || validPages[validPages.length - 1] || validPages[0]

    if (onUpload) {
      onUpload({
        name: finalName,
        category,
        associatedProviderId: selectedProviderId || associatedProviderId || null,
        associatedProviderName: providerName || associatedProviderName || '',
        documentDate: docDate,
        appointmentId: appointmentId || null,
        pages: validPages,
        dataUrl: targetPage?.dataUrl || null,
        file: uploadedFiles[0],
        files: uploadedFiles,
      })
    }
  }

  const realCapturedPages = pages.filter(p => p.dataUrl || p.file)
  const activePreviewPage =
    pages.find(p => p.id === activePreviewPageId && p.dataUrl) || [...pages].reverse().find(p => p.dataUrl) || null

  return (
    <div
      className="modal show d-block camera-scan-modal"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)', zIndex: 1050, overflowY: 'auto' }}
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content shadow-lg border-0">
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title font-weight-bold mb-0">
              <FAIcon name="camera" className="mr-2 text-primary" />
              Scan Document with Camera
            </h5>
            <button type="button" className="close" onClick={onHide} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {/* HTML5 Native Mobile Camera Capture Input */}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="camera-native-input"
              ref={nativeInputRef}
              className="d-none"
              onChange={handleFileCapture}
            />

            {permissionError ? (
              <div className="alert alert-warning permission-fallback-banner" role="alert">
                <FAIcon name="exclamation-triangle" className="mr-2" />
                <strong>Camera access blocked.</strong> Please allow camera permissions or upload an image file
                directly.
                <input
                  type="file"
                  accept="image/*"
                  data-testid="file-fallback-input"
                  ref={fallbackInputRef}
                  className="form-control-file mt-2"
                  onChange={handleFileCapture}
                />
              </div>
            ) : (
              <div className="camera-viewfinder-container mb-3">
                {/* Camera Orientation, Rotate, Zoom & Crop Toolbar */}
                <div
                  className="camera-toolbar bg-light border rounded p-2 mb-2 d-flex flex-wrap align-items-center justify-content-between"
                  style={{ gap: '8px' }}
                >
                  {/* Portrait vs Landscape Mode */}
                  <div className="btn-group btn-group-sm" role="group" aria-label="Scan Orientation">
                    <button
                      type="button"
                      className={`btn ${orientation === 'portrait' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handleOrientationToggle('portrait')}
                      title="Portrait Document (8.5 x 11)"
                    >
                      <FAIcon name="file-alt" className="mr-1" /> Portrait
                    </button>
                    <button
                      type="button"
                      className={`btn ${orientation === 'landscape' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handleOrientationToggle('landscape')}
                      title="Landscape Document / Slip"
                    >
                      <FAIcon name="image" className="mr-1" /> Landscape
                    </button>
                  </div>

                  {/* Rotate & Mirror Controls */}
                  <div className="btn-group btn-group-sm" role="group" aria-label="Rotate Camera">
                    <button
                      type="button"
                      className="btn btn-outline-secondary rotate-left-btn"
                      onClick={handleRotateLeft}
                      title="Rotate Left 90°"
                    >
                      <FAIcon name="undo" className="mr-1" /> -90°
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary rotate-right-btn"
                      onClick={handleRotateRight}
                      title="Rotate Right 90°"
                    >
                      <FAIcon name="redo" className="mr-1" /> +90°
                    </button>
                    <button
                      type="button"
                      className={`btn ${flipHorizontal ? 'btn-info' : 'btn-outline-secondary'}`}
                      onClick={() => setFlipHorizontal(prev => !prev)}
                      title="Flip / Un-mirror Camera Horizontally"
                    >
                      <FAIcon name="exchange-alt" className="mr-1" /> Mirror
                    </button>
                  </div>

                  {/* Zoom Controls */}
                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <small className="text-muted font-weight-bold mb-0">Zoom:</small>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-0 px-2 zoom-out-btn"
                      onClick={() => handleZoomStep(-0.25)}
                      disabled={zoom <= 1.0}
                      title="Zoom Out"
                    >
                      -
                    </button>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.1"
                      value={zoom}
                      onChange={e => setZoom(parseFloat(e.target.value) || 1.0)}
                      style={{ width: '85px' }}
                      aria-label="Camera Zoom"
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-0 px-2 zoom-in-btn"
                      onClick={() => handleZoomStep(0.25)}
                      disabled={zoom >= 3.0}
                      title="Zoom In"
                    >
                      +
                    </button>
                    <span className="badge badge-secondary" style={{ minWidth: '38px' }}>
                      {zoom.toFixed(1)}x
                    </span>
                  </div>

                  {/* Crop Adjustment Toggle */}
                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <button
                      type="button"
                      className={`btn btn-sm ${showCropSliders ? 'btn-info' : 'btn-outline-secondary'}`}
                      onClick={() => setShowCropSliders(prev => !prev)}
                      title="Adjust crop frame margins"
                    >
                      <FAIcon name="crop" className="mr-1" /> Crop Area
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${enhanceContrast ? 'btn-dark' : 'btn-outline-secondary'}`}
                      onClick={() => setEnhanceContrast(prev => !prev)}
                      title="Toggle high-contrast document enhancement"
                    >
                      <FAIcon name="adjust" className="mr-1" /> {enhanceContrast ? 'Enhanced' : 'Original'}
                    </button>
                  </div>
                </div>

                {/* Expandable Crop Preset & Margin Fine-Tuning Panel */}
                {showCropSliders && (
                  <div className="crop-controls-panel bg-light border rounded p-2 mb-2">
                    <div
                      className="d-flex flex-wrap align-items-center justify-content-between mb-2"
                      style={{ gap: '6px' }}
                    >
                      <small className="font-weight-bold text-secondary">
                        <FAIcon name="crop" className="mr-1" /> Crop Frame Presets (or drag green corners on camera
                        view):
                      </small>
                      <div className="btn-group btn-group-sm">
                        {Object.entries(CROP_PRESETS).map(([key, preset]) => (
                          <button
                            key={key}
                            type="button"
                            className={`btn ${cropPreset === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => handlePresetSelect(key)}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="row text-center small">
                      <div className="col-3">
                        <label htmlFor="cropTopRange" className="mb-0 d-block">
                          Top ({cropInsets.top}%)
                        </label>
                        <input
                          id="cropTopRange"
                          type="range"
                          min="2"
                          max="40"
                          value={cropInsets.top}
                          onChange={e => {
                            setCropPreset('custom')
                            setCropInsets(prev => ({ ...prev, top: parseInt(e.target.value, 10) || 5 }))
                          }}
                          className="w-100"
                        />
                      </div>
                      <div className="col-3">
                        <label htmlFor="cropBottomRange" className="mb-0 d-block">
                          Bottom ({cropInsets.bottom}%)
                        </label>
                        <input
                          id="cropBottomRange"
                          type="range"
                          min="2"
                          max="40"
                          value={cropInsets.bottom}
                          onChange={e => {
                            setCropPreset('custom')
                            setCropInsets(prev => ({ ...prev, bottom: parseInt(e.target.value, 10) || 5 }))
                          }}
                          className="w-100"
                        />
                      </div>
                      <div className="col-3">
                        <label htmlFor="cropLeftRange" className="mb-0 d-block">
                          Left ({cropInsets.left}%)
                        </label>
                        <input
                          id="cropLeftRange"
                          type="range"
                          min="2"
                          max="40"
                          value={cropInsets.left}
                          onChange={e => {
                            setCropPreset('custom')
                            setCropInsets(prev => ({ ...prev, left: parseInt(e.target.value, 10) || 5 }))
                          }}
                          className="w-100"
                        />
                      </div>
                      <div className="col-3">
                        <label htmlFor="cropRightRange" className="mb-0 d-block">
                          Right ({cropInsets.right}%)
                        </label>
                        <input
                          id="cropRightRange"
                          type="range"
                          min="2"
                          max="40"
                          value={cropInsets.right}
                          onChange={e => {
                            setCropPreset('custom')
                            setCropInsets(prev => ({ ...prev, right: parseInt(e.target.value, 10) || 5 }))
                          }}
                          className="w-100"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Capture Status Banner */}
                {captureBanner && (
                  <div className="alert alert-success py-1 px-3 mb-2 d-flex justify-content-between align-items-center">
                    <span>
                      <FAIcon name="check-circle" className="mr-2" />
                      <strong>{captureBanner}</strong>
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-success p-0"
                      onClick={() => setShowLiveCamera(prev => !prev)}
                    >
                      {showLiveCamera ? 'View Captured Page' : 'Back to Live Camera'}
                    </button>
                  </div>
                )}

                {/* Captured Page Preview Panel */}
                {!showLiveCamera && activePreviewPage && (
                  <div
                    className="captured-page-preview-panel position-relative border rounded p-3 text-center"
                    style={{ background: '#1e2227', minHeight: '380px' }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2 text-white">
                      <span className="badge badge-success px-2 py-1">
                        <FAIcon name="check" className="mr-1" /> Previewing {activePreviewPage.name}
                      </span>
                      <div className="btn-group btn-group-sm">
                        <button
                          type="button"
                          className="btn btn-outline-light"
                          onClick={() => handleRotateCapturedPage(activePreviewPage.id, -90)}
                          title="Rotate Captured Page Left 90°"
                        >
                          <FAIcon name="undo" className="mr-1" /> Rotate Left
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-light"
                          onClick={() => handleRotateCapturedPage(activePreviewPage.id, 90)}
                          title="Rotate Captured Page Right 90°"
                        >
                          <FAIcon name="redo" className="mr-1" /> Rotate Right
                        </button>
                        <button
                          type="button"
                          className="btn btn-warning font-weight-bold"
                          onClick={() => {
                            setRetakingPageId(activePreviewPage.id)
                            setShowLiveCamera(true)
                          }}
                          title="Retake and replace this page with camera"
                        >
                          <FAIcon name="sync-alt" className="mr-1" /> Retake Page
                        </button>
                        <button
                          type="button"
                          className="btn btn-info font-weight-bold"
                          onClick={() => {
                            setRetakingPageId(null)
                            setShowLiveCamera(true)
                          }}
                          title="Scan an additional page with camera"
                        >
                          <FAIcon name="plus" className="mr-1" /> Add Page
                        </button>
                      </div>
                    </div>
                    <img
                      src={activePreviewPage.dataUrl}
                      alt={activePreviewPage.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '380px',
                        objectFit: 'contain',
                        borderRadius: '4px',
                        border: '2px solid #28a745',
                        background: '#fff',
                      }}
                    />
                  </div>
                )}

                {/* Live Camera Viewfinder (kept mounted so streamRef stays bound to videoRef) */}
                <div
                  ref={viewportBoxRef}
                  className="video-viewport position-relative"
                  style={{
                    background: '#111',
                    height: orientation === 'portrait' ? '420px' : '320px',
                    color: '#fff',
                    textAlign: 'center',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    display: !showLiveCamera && activePreviewPage ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <video
                    ref={videoRef}
                    playsInline
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      transform: `rotate(${rotation}deg) scale(${flipHorizontal ? -zoom : zoom}, ${zoom})`,
                      transition: 'transform 0.15s ease-out',
                    }}
                  >
                    <track kind="captions" />
                  </video>

                  {/* Shutter Flash Effect */}
                  {shutterFlash && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        zIndex: 10,
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                  {/* Interactive Crop Box Overlay with Draggable Corners */}
                  <div
                    className="viewfinder-overlay"
                    style={{
                      position: 'absolute',
                      top: `${cropInsets.top}%`,
                      bottom: `${cropInsets.bottom}%`,
                      left: `${cropInsets.left}%`,
                      right: `${cropInsets.right}%`,
                      border: '2px dashed #28a745',
                      borderRadius: '6px',
                      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
                      pointerEvents: 'none',
                    }}
                  >
                    <span
                      className="badge badge-dark"
                      style={{
                        position: 'absolute',
                        top: '6px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        opacity: 0.85,
                        fontSize: '11px',
                      }}
                    >
                      Align {orientation === 'portrait' ? 'Portrait Page (8.5×11)' : 'Landscape Document'} Inside Frame
                    </span>

                    {/* 4 Interactive Corner Drag Handles */}
                    {[
                      { key: 'top-left', style: { top: '-6px', left: '-6px', cursor: 'nwse-resize' } },
                      { key: 'top-right', style: { top: '-6px', right: '-6px', cursor: 'nesw-resize' } },
                      { key: 'bottom-left', style: { bottom: '-6px', left: '-6px', cursor: 'nesw-resize' } },
                      { key: 'bottom-right', style: { bottom: '-6px', right: '-6px', cursor: 'nwse-resize' } },
                    ].map(handle => (
                      <div
                        key={handle.key}
                        role="presentation"
                        onMouseDown={e => handleCornerMouseDown(handle.key, e)}
                        style={{
                          position: 'absolute',
                          width: '14px',
                          height: '14px',
                          backgroundColor: '#28a745',
                          border: '2px solid #fff',
                          borderRadius: '3px',
                          pointerEvents: 'auto',
                          ...handle.style,
                        }}
                        title="Drag corner to adjust crop area"
                      />
                    ))}
                  </div>
                </div>

                {/* Hidden canvas for video frame extraction */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {/* Viewfinder Action Controls & Mobile Native Trigger */}
                <div
                  className="viewfinder-controls mt-2 d-flex flex-wrap justify-content-between align-items-center"
                  style={{ gap: '8px' }}
                >
                  <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-success capture-btn font-weight-bold"
                      onClick={handleCaptureFrame}
                      disabled={isCapturing}
                    >
                      <FAIcon name="camera" className="mr-1" />
                      {isCapturing
                        ? 'Capturing...'
                        : realCapturedPages.length > 0
                        ? `Capture Page ${realCapturedPages.length + 1}`
                        : 'Capture Page'}
                    </button>
                    {!showLiveCamera && (
                      <button type="button" className="btn btn-outline-primary" onClick={() => setShowLiveCamera(true)}>
                        <FAIcon name="video" className="mr-1" /> Live Camera View
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => nativeInputRef.current?.click()}
                      title="Use mobile device native camera or choose image file"
                    >
                      <FAIcon name="mobile-alt" className="mr-1" /> Device Camera / File
                    </button>
                  </div>
                  <small className="text-muted font-weight-bold">
                    {pages.length} {pages.length === 1 ? 'page' : 'pages'} staged
                    {realCapturedPages.length > 0 ? ` (${realCapturedPages.length} captured)` : ''}
                  </small>
                </div>

                {/* Multi-page document thumbnail carousel with click-to-preview, reordering, and discard */}
                {pages.length > 0 && (
                  <div className="captured-pages-strip mt-2 d-flex flex-wrap" style={{ gap: '8px' }}>
                    {pages.map((p, idx) => {
                      const isSelected = activePreviewPage && activePreviewPage.id === p.id && !showLiveCamera
                      return (
                        <div
                          key={p.id}
                          className={`page-thumbnail p-2 border rounded d-flex align-items-center ${
                            isSelected ? 'border-success bg-white shadow-sm' : 'bg-light'
                          }`}
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0 d-flex align-items-center text-dark text-decoration-none"
                            style={{ cursor: p.dataUrl ? 'pointer' : 'default' }}
                            onClick={() => {
                              if (p.dataUrl) {
                                setActivePreviewPageId(p.id)
                                setShowLiveCamera(false)
                              }
                            }}
                          >
                            {p.dataUrl && (
                              <img
                                src={p.dataUrl}
                                alt={`Page ${idx + 1}`}
                                style={{
                                  width: '42px',
                                  height: '54px',
                                  objectFit: 'cover',
                                  borderRadius: '3px',
                                  marginRight: '8px',
                                  border: '1px solid #ccc',
                                }}
                              />
                            )}
                            <span className="font-weight-bold small">
                              Page {idx + 1}
                              {!p.dataUrl && !p.file ? ' (Ready)' : ''}
                            </span>
                          </button>
                          {p.dataUrl && (
                            <button
                              type="button"
                              className="btn btn-xs btn-link text-secondary p-0 ml-2"
                              onClick={e => {
                                e.stopPropagation()
                                handleRotateCapturedPage(p.id, 90)
                              }}
                              title="Rotate this page 90°"
                            >
                              <FAIcon name="redo" />
                            </button>
                          )}
                          {idx > 0 && (
                            <button
                              type="button"
                              className="btn btn-xs btn-link text-muted p-0 ml-1 reorder-page-btn"
                              onClick={e => {
                                e.stopPropagation()
                                handleMovePage(idx, idx - 1)
                              }}
                              title="Move page earlier"
                            >
                              <FAIcon name="arrow-left" />
                            </button>
                          )}
                          {idx < pages.length - 1 && (
                            <button
                              type="button"
                              className="btn btn-xs btn-link text-muted p-0 ml-1 reorder-page-btn"
                              onClick={e => {
                                e.stopPropagation()
                                handleMovePage(idx, idx + 1)
                              }}
                              title="Move page later"
                            >
                              <FAIcon name="arrow-right" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-xs btn-danger ml-2 delete-page-btn"
                            onClick={e => {
                              e.stopPropagation()
                              handleDeletePage(p.id)
                            }}
                            title="Delete page"
                          >
                            &times;
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {uploadError && (
              <div className="alert alert-danger scan-upload-error-banner mb-3" role="alert">
                <FAIcon name="exclamation-circle" className="mr-2" />
                {uploadError}
              </div>
            )}

            {/* Document Metadata Prompt Form */}
            <div className="metadata-prompt-form border-top pt-3">
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label htmlFor="docCategory">Document Category</label>
                  <select id="docCategory" className="form-control" value={category} onChange={handleCategoryChange}>
                    <option value="Work Status">Work Status</option>
                    <option value="Encounter Note">Encounter Note</option>
                    <option value="Diagnostic Report">Diagnostic Report</option>
                    <option value="Prescription">Prescription</option>
                    <option value="Billing/Fee Ticket">Billing/Fee Ticket</option>
                    <option value="Billing">Billing</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group col-md-6">
                  <label htmlFor="docProvider">Associated Provider</label>
                  {providers && providers.length > 0 ? (
                    <select
                      id="docProvider"
                      className="form-control"
                      value={selectedProviderId || ''}
                      onChange={handleProviderSelectChange}
                    >
                      <option value="">-- Select Linked Provider (Optional) --</option>
                      {providers.map(p => (
                        <option key={p.id || p.name} value={p.id || ''}>
                          {p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      id="docProvider"
                      className="form-control"
                      value={providerName}
                      onChange={handleProviderNameChange}
                      placeholder="Enter provider or clinic name"
                    />
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-8">
                  <label htmlFor="docName">Document Name</label>
                  <input
                    type="text"
                    id="docName"
                    ref={nameInputRef}
                    className="form-control"
                    value={docName}
                    onChange={handleNameChange}
                    placeholder="Enter document name"
                  />
                  <small className="form-text text-muted">Auto-suggested format: Category - Provider - Date</small>
                </div>

                <div className="form-group col-md-4">
                  <label htmlFor="docDate">Document Date</label>
                  <input
                    type="date"
                    id="docDate"
                    className="form-control"
                    value={docDate}
                    onChange={handleDateChange}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onHide}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary submit-scan-btn font-weight-bold"
              onClick={handleSubmit}
              disabled={realCapturedPages.length === 0}
            >
              <FAIcon name="cloud-upload-alt" className="mr-1" />
              Upload & Process HITL
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CameraScanModal
